/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Loftsails Variant Picker
 * ========================
 * JavaScript for the sail variant picker component.
 *
 * Version: 1.3.1
 * Last updated: 2026-09-01
 */

(function() {
  'use strict';

  if (window.LoftsailsVariantPicker) return;

  /**
   * LoftsailsVariantPicker Class
   * Handles all variant picker functionality
   */
  class LoftsailsVariantPicker {
    constructor(container) {
      if (container.loftsailsPicker) return container.loftsailsPicker;
      this.events = new AbortController();
      this.container = container;
      this.sectionId = container.dataset.sectionId;

      // Get data from JSON script tag
      const dataScript = document.getElementById(`lsvp-data-${this.sectionId}`);
      if (!dataScript) {
        console.warn('LSVP: No data found for section', this.sectionId);
        return;
      }

      try {
        this.data = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('LSVP: Failed to parse data', e);
        return;
      }

      container.loftsailsPicker = this;
      this.strings = this.data.strings || {};

      // State
      this.combinedOptions = Boolean(this.data.combinedOptions);
      this.showConstruction = Boolean(this.data.showConstruction);
      this.showColor = Boolean(this.data.showColor);
      this.selectedSize = this.data.currentProduct.size;
      this.selectedConstruction = this.data.currentProduct.construction || '';
      this.selectedColor = this.data.currentProduct.color || '';

      // Elements
      this.modal = document.getElementById(`lsvp-modal-${this.sectionId}`);
      this.sizeButtons = this.container.querySelectorAll('.lsvp__size-btn');
      this.constructionButtons = this.container.querySelectorAll('.lsvp__construction-btn:not([data-option])');
      this.colorButtons = this.container.querySelectorAll('.lsvp__color-btn');
      this.optionButtons = this.container.querySelectorAll('[data-option]');
      this.expressVariantInput = document.getElementById(`lsvp-variant-input-${this.sectionId}`);
      this.expressCheckout = document.getElementById(`lsvp-express-${this.sectionId}`);
      this.bisContainer = document.getElementById(`lsvp-bis-${this.sectionId}`);
      this.comingSoonContainer = document.getElementById(`lsvp-coming-soon-${this.sectionId}`);
      this.race2027Pdp = Boolean(this.data.race2027Pdp);

      // Initialize
      if (this.combinedOptions) {
        [...this.sizeButtons].sort((a, b) => Number(a.dataset.size) - Number(b.dataset.size))
          .forEach(button => button.parentElement.appendChild(button));
      }
      this.bindEvents();
      this.updateAvailability();
      this.updateConfirmButton();
    }

    destroy() {
      this.closeModal();
      this.events.abort();
      delete this.container.loftsailsPicker;
    }

    translate(key, fallback) {
      return this.strings[key] || fallback;
    }

    unitsLeft(count) {
      return this.translate('unitsLeft', '__COUNT__ left').replace('__COUNT__', count);
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Open modal buttons
      this.container.querySelectorAll('[data-lsvp-open="modal"]').forEach(btn => {
        btn.addEventListener('click', () => this.openModal(btn));
      });

      // Close modal button
      this.container.querySelectorAll('[data-lsvp-close]').forEach(btn => {
        btn.addEventListener('click', () => this.closeModal());
      });

      // Close on overlay click
      if (this.modal) {
        this.modal.addEventListener('keydown', event => this.trapModalFocus(event));
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) {
            this.closeModal();
          }
        });
      }

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isModalOpen()) {
          this.closeModal();
        }
      }, { signal: this.events.signal });

      this.optionButtons.forEach(btn => {
        btn.addEventListener('click', () => this.selectOption(btn.dataset.construction || '', btn.dataset.color || ''));
      });

      // Size button clicks
      this.sizeButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.selectSize(btn.dataset.size);
        });
      });

      // Construction button clicks
      this.constructionButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.selectConstruction(btn.dataset.construction || '');
        });
      });

      // Color button clicks
      this.colorButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.selectColor(btn.dataset.color || '');
        });
      });

      // Confirm button
      this.container.querySelectorAll('[data-lsvp-confirm]').forEach(btn => {
        btn.addEventListener('click', () => this.confirmSelection());
      });

      // View specs link: defer to the sticky nav's "Specs" pill so it opens
      // the same specs modal (or scrolls) instead of jumping to a bare anchor.
      this.container.querySelectorAll('.lsvp__specs-link').forEach(link => {
        link.addEventListener('click', (e) => {
          const specsBtn = document.querySelector('[data-label="Specs"]');
          if (specsBtn) {
            e.preventDefault();
            specsBtn.click();
          }
        });
      });
    }

    /**
     * Open the modal
     */
    openModal(opener = document.activeElement) {
      if (!this.modal || this.isModalOpen()) return;

      this.ensureModalSentinel();
      this.modalOpener = opener;
      this.previousBodyOverflow = document.body.style.overflow;
      this.modal.inert = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';

      const firstChoice = this.modal.querySelector(this.combinedOptions ? '[data-option]' : '.lsvp__size-btn:not(.out-of-stock)');
      const focusTarget = firstChoice || this.modalFocusableElements()[0];
      if (focusTarget) focusTarget.focus();
    }

    ensureModalSentinel() {
      if (this.focusSentinel) return;
      const dialog = this.modal.querySelector('[role="dialog"]');
      if (!dialog) return;
      this.focusSentinel = document.createElement('span');
      this.focusSentinel.tabIndex = 0;
      this.focusSentinel.className = 'sr-only';
      this.focusSentinel.dataset.lsvpFocusSentinel = '';
      this.focusSentinel.addEventListener('focus', () => {
        if (this.backwardSentinelFocus || !this.isModalOpen()) return;
        this.modalFocusableElements()[0]?.focus();
      });
      dialog.appendChild(this.focusSentinel);
    }

    modalFocusableElements() {
      return [...this.modal.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter(element => element !== this.focusSentinel && !element.disabled && element.tabIndex >= 0 &&
          !element.closest('[hidden], [inert], [aria-hidden="true"]') && element.getClientRects().length > 0);
    }

    trapModalFocus(event) {
      if (event.key !== 'Tab' || !this.isModalOpen()) return;
      const focusable = this.modalFocusableElements();
      const first = focusable[0];
      if (!first) {
        event.preventDefault();
        return;
      }
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !this.modal.contains(active))) {
        if (!this.focusSentinel) {
          event.preventDefault();
          return;
        }
        // Native reverse Tab can enter Shopify's closed shadow checkout controls.
        this.backwardSentinelFocus = true;
        try {
          this.focusSentinel.focus();
        } finally {
          this.backwardSentinelFocus = false;
        }
      } else if (!event.shiftKey && !this.modal.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    }

    /**
     * Close the modal and return focus to the control that opened it.
     */
    closeModal() {
      if (!this.modal || !this.isModalOpen()) return;

      // Closing cancels unconfirmed picks; the page's purchase form still owns this SKU.
      this.selectedSize = this.data.currentProduct.size;
      this.selectedConstruction = this.data.currentProduct.construction || '';
      this.selectedColor = this.data.currentProduct.color || '';
      this.refreshSelection();
      this.updateSummaryDisplay();

      window.LoftsailsProductSwap?.cancel();
      if (this.modalOpener?.isConnected) this.modalOpener.focus({ preventScroll: true });
      this.modal.inert = true;
      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.classList.remove('is-open');
      document.body.style.overflow = this.previousBodyOverflow;
      this.modalOpener = null;
    }

    /**
     * Check if modal is open
     */
    isModalOpen() {
      return this.modal && this.modal.classList.contains('is-open');
    }

    /**
     * Select a size
     */
    selectOption(construction, color) {
      this.selectedConstruction = construction;
      this.selectedColor = color;
      if (!this.findVariant(this.selectedSize, construction, color)) this.selectedSize = '';
      this.refreshSelection();
    }

    selectSize(size) {
      this.selectedSize = size;
      if (!this.combinedOptions) this.syncSelectionToAvailable({ size });
      this.refreshSelection();
    }

    /**
     * Select a construction
     */
    selectConstruction(construction) {
      this.selectedConstruction = construction || '';
      this.syncSelectionToAvailable({ construction: this.selectedConstruction });
      this.refreshSelection();
    }

    /**
     * Select a color
     */
    selectColor(color) {
      this.selectedColor = color || '';
      this.syncSelectionToAvailable({ color: this.selectedColor });
      this.refreshSelection();
    }

    /**
     * Update selected button states and dependent UI
     */
    refreshSelection() {
      this.optionButtons.forEach(btn => {
        const selected = (btn.dataset.construction || '') === this.selectedConstruction && (btn.dataset.color || '') === this.selectedColor;
        btn.classList.toggle('selected', selected);
      });
      this.sizeButtons.forEach(btn => {
        btn.classList.toggle('selected', btn.dataset.size === this.selectedSize);
      });

      this.constructionButtons.forEach(btn => {
        btn.classList.toggle('selected', (btn.dataset.construction || '') === this.selectedConstruction);
      });

      this.colorButtons.forEach(btn => {
        btn.classList.toggle('selected', (btn.dataset.color || '') === this.selectedColor);
      });

      this.updateAvailability();
      this.updateExpressCheckout();
      this.updateConfirmButton();
      this.updatePriceDisplay();
      // The page summary belongs to the committed product, not the draft.
    }

    /**
     * Move hidden or invalid axis selections to an available product.
     */
    syncSelectionToAvailable(anchor = {}) {
      const selectedVariant = this.findVariant(
        this.selectedSize,
        this.selectedConstruction,
        this.selectedColor
      );

      if (selectedVariant) return;

      const fallback = this.data.variants.find(variant => {
        if (!variant.available) return false;
        if (anchor.size !== undefined && variant.size !== anchor.size) return false;
        if (anchor.construction !== undefined && this.showConstruction && variant.construction !== anchor.construction) return false;
        if (anchor.color !== undefined && this.showColor && variant.color !== anchor.color) return false;
        return true;
      });

      if (!fallback) return;

      this.selectedSize = fallback.size;
      this.selectedConstruction = fallback.construction || '';
      this.selectedColor = fallback.color || '';
    }

    /**
     * Find a variant by the visible axes
     */
    findVariant(size, construction, color) {
      return this.data.variants.find(variant => {
        if (variant.size !== size) return false;
        if ((this.combinedOptions || this.showConstruction) && (variant.construction || '') !== (construction || '')) return false;
        if ((this.combinedOptions || this.showColor) && (variant.color || '') !== (color || '')) return false;
        return true;
      });
    }

    /**
     * Find any variant for a candidate axis value, constrained by other visible selections
     */
    findVariantForCandidate(candidate) {
      return this.data.variants.find(variant => {
        if (candidate.size !== undefined && variant.size !== candidate.size) return false;
        if (candidate.construction !== undefined && (variant.construction || '') !== (candidate.construction || '')) return false;
        if (candidate.color !== undefined && (variant.color || '') !== (candidate.color || '')) return false;
        if (candidate.size === undefined && variant.size !== this.selectedSize) return false;
        if (candidate.construction === undefined && (this.combinedOptions || this.showConstruction) && (variant.construction || '') !== this.selectedConstruction) return false;
        if (candidate.color === undefined && (this.combinedOptions || this.showColor) && (variant.color || '') !== this.selectedColor) return false;
        return true;
      });
    }

    isComingSoonVariant(variant) {
      return Boolean(this.race2027Pdp && variant && variant.available === false);
    }

    unavailableStockLabel(variant) {
      return this.isComingSoonVariant(variant)
        ? this.translate('comingSoon', 'Coming soon')
        : this.translate('soldOut', 'Sold out');
    }

    applyUnavailableButtonClasses(button, variant) {
      if (!button) return;
      const comingSoon = this.isComingSoonVariant(variant);
      button.classList.toggle('coming-soon', comingSoon);
      button.classList.toggle('out-of-stock', Boolean(variant && !variant.available && !comingSoon));
      button.classList.toggle('unavailable', Boolean(!variant || (!variant.available && !comingSoon)));
    }

    /**
     * Update all visible availability indicators
     */
    updateAvailability() {
      this.updateSizeAvailability();
      this.updateConstructionAvailability();
      this.updateColorAvailability();
      this.updateAccessibleNames();
    }

    /**
     * Keep accessible names in sync with visible stock text and selection.
     * Liquid renders the initial aria-label; every availability or selection
     * change refreshes it here so screen readers never announce a stale
     * state. Variant buttons stay focusable (aria-disabled only, never
     * native disabled) so sold-out and coming-soon options remain reachable
     * for back-in-stock and coming-soon flows.
     */
    updateAccessibleNames() {
      const selectedSuffix = (isSelected) => (isSelected ? `, ${this.translate('selected', 'Selected')}` : '');
      // Locale-owned separator: ": " in English, non-breaking space + ": " in French.
      const separator = this.translate('statusSeparator', ': ');

      this.sizeButtons.forEach(btn => {
        const stockEl = btn.querySelector('.lsvp__size-stock');
        const stock = stockEl ? stockEl.textContent.trim() : '';
        const isSelected = btn.dataset.size === this.selectedSize;
        btn.setAttribute('aria-label', `${btn.dataset.size}m²${separator}${stock}${selectedSuffix(isSelected)}`);
      });

      this.constructionButtons.forEach(btn => {
        const construction = btn.dataset.construction || '';
        const stockEl = btn.querySelector('.lsvp__construction-stock');
        const stock = stockEl ? stockEl.textContent.trim() : '';
        const isSelected = construction === this.selectedConstruction;
        const label = construction || this.translate('standard', 'Standard');
        btn.setAttribute('aria-label', `${label}${separator}${stock}${selectedSuffix(isSelected)}`);
      });

      this.colorButtons.forEach(btn => {
        const color = btn.dataset.color || '';
        const stockEl = btn.querySelector('.lsvp__color-stock');
        const stock = stockEl ? stockEl.textContent.trim() : '';
        const isSelected = color === this.selectedColor;
        const label = color || this.translate('default', 'Default');
        btn.setAttribute('aria-label', `${label}${separator}${stock}${selectedSuffix(isSelected)}`);
      });

      this.optionButtons.forEach(btn => {
        const construction = btn.dataset.construction || '';
        const color = btn.dataset.color || '';
        const isSelected = construction === this.selectedConstruction && color === this.selectedColor;
        btn.setAttribute('aria-pressed', String(isSelected));
        let label = [construction, color].filter(Boolean).join(' ');
        if (color === 'Green') label = this.translate('ltdGreen', 'LTD Green');
        btn.setAttribute('aria-label', `${label}${selectedSuffix(isSelected)}`);
      });
    }

    /**
     * Update size button availability based on selected color
     */
    updateSizeAvailability() {
      this.sizeButtons.forEach(btn => {
        const size = btn.dataset.size;
        const variant = this.findVariantForCandidate({ size });
        if (this.combinedOptions) btn.setAttribute('aria-pressed', String(size === this.selectedSize));

        const stockEl = btn.querySelector('.lsvp__size-stock');
        const priceEl = btn.querySelector('[data-lsvp-size-price]');
        if (priceEl) {
          priceEl.textContent = variant ? variant.priceFormatted : '';
        }
        if (!stockEl) return;

        // Reset classes
        stockEl.classList.remove('in-stock', 'low-stock', 'out-of-stock', 'coming-soon');

        // Hide combos that have no product at all; only sold-out ones stay visible.
        btn.hidden = !variant;

        if (!variant) {
          stockEl.textContent = this.translate('notAvailable', 'Not available');
          stockEl.classList.add('out-of-stock');
          btn.classList.add('unavailable');
          btn.classList.remove('coming-soon');
          btn.setAttribute('aria-disabled', 'true');
        } else if (!variant.available) {
          stockEl.textContent = this.unavailableStockLabel(variant);
          stockEl.classList.add(this.isComingSoonVariant(variant) ? 'coming-soon' : 'out-of-stock');
          this.applyUnavailableButtonClasses(btn, variant);
          btn.setAttribute('aria-disabled', this.isComingSoonVariant(variant) ? 'false' : String(!this.combinedOptions));
        } else if (variant.inventory <= 3) {
          stockEl.textContent = this.unitsLeft(variant.inventory);
          stockEl.classList.add('low-stock');
          btn.classList.remove('unavailable', 'coming-soon', 'out-of-stock');
          btn.setAttribute('aria-disabled', 'false');
        } else {
          stockEl.textContent = this.translate('inStock', 'In stock');
          stockEl.classList.add('in-stock');
          btn.classList.remove('unavailable', 'coming-soon', 'out-of-stock');
          btn.setAttribute('aria-disabled', 'false');
        }
      });
    }

    /**
     * Update construction button availability based on selected size/color
     */
    updateConstructionAvailability() {
      this.constructionButtons.forEach(btn => {
        const construction = btn.dataset.construction || '';
        const variant = this.findVariantForCandidate({ construction });

        const stockEl = btn.querySelector('.lsvp__construction-stock');
        if (!stockEl) return;

        stockEl.classList.remove('in-stock', 'low-stock', 'out-of-stock', 'coming-soon');

        if (!variant) {
          stockEl.textContent = this.translate('notAvailable', 'Not available');
          stockEl.classList.add('out-of-stock');
          btn.classList.add('unavailable');
          btn.classList.remove('coming-soon');
          btn.setAttribute('aria-disabled', 'true');
        } else if (!variant.available) {
          stockEl.textContent = this.unavailableStockLabel(variant);
          stockEl.classList.add(this.isComingSoonVariant(variant) ? 'coming-soon' : 'out-of-stock');
          this.applyUnavailableButtonClasses(btn, variant);
          btn.setAttribute('aria-disabled', this.isComingSoonVariant(variant) ? 'false' : 'true');
        } else if (variant.inventory <= 3) {
          stockEl.textContent = this.unitsLeft(variant.inventory);
          stockEl.classList.add('low-stock');
          btn.classList.remove('unavailable', 'coming-soon', 'out-of-stock');
          btn.setAttribute('aria-disabled', 'false');
        } else {
          stockEl.textContent = this.translate('inStock', 'In stock');
          stockEl.classList.add('in-stock');
          btn.classList.remove('unavailable', 'coming-soon', 'out-of-stock');
          btn.setAttribute('aria-disabled', 'false');
        }
      });
    }

    /**
     * Update color button availability based on selected size/construction
     */
    updateColorAvailability() {
      this.colorButtons.forEach(btn => {
        const color = btn.dataset.color || '';
        const variant = this.findVariantForCandidate({ color });

        const stockEl = btn.querySelector('.lsvp__color-stock');
        if (!stockEl) return;

        // Reset classes
        stockEl.classList.remove('in-stock', 'low-stock', 'out-of-stock', 'coming-soon');

        if (!variant) {
          stockEl.textContent = this.translate('notAvailable', 'Not available');
          stockEl.classList.add('out-of-stock');
          btn.classList.add('unavailable');
          btn.classList.remove('coming-soon');
          btn.style.opacity = '0.4';
          btn.setAttribute('aria-disabled', 'true');
        } else if (!variant.available) {
          stockEl.textContent = this.unavailableStockLabel(variant);
          stockEl.classList.add(this.isComingSoonVariant(variant) ? 'coming-soon' : 'out-of-stock');
          if (this.isComingSoonVariant(variant)) {
            btn.classList.remove('unavailable');
            btn.classList.add('coming-soon');
            btn.style.opacity = '1';
            btn.setAttribute('aria-disabled', 'false');
          } else {
            btn.classList.add('unavailable');
            btn.classList.remove('coming-soon');
            btn.style.opacity = '0.4';
            btn.setAttribute('aria-disabled', 'true');
          }
        } else if (variant.inventory <= 3) {
          stockEl.textContent = this.unitsLeft(variant.inventory);
          stockEl.classList.add('low-stock');
          btn.classList.remove('unavailable');
          btn.style.opacity = '1';
          btn.setAttribute('aria-disabled', 'false');
        } else {
          stockEl.textContent = this.translate('inStock', 'In stock');
          stockEl.classList.add('in-stock');
          btn.classList.remove('unavailable');
          btn.style.opacity = '1';
          btn.setAttribute('aria-disabled', 'false');
        }
      });
    }

    /**
     * Confirm selection — navigate to linked product or add current product to cart
     */
    async confirmSelection() {
      const variant = this.findVariant(this.selectedSize, this.selectedConstruction, this.selectedColor);

      if (!variant) {
        console.warn('LSVP: No variant found for', this.selectedSize, this.selectedConstruction, this.selectedColor);
        return;
      }

      if (!variant.available) {
        console.warn('LSVP: Selected variant is not available');
        return;
      }

      if (this.isDifferentProduct(variant)) {
        const destination = new URL(variant.url, window.location.origin);
        destination.searchParams.set('variant', variant.variantId);
        const error = this.container.querySelector('[data-lsvp-error]');
        if (error) error.hidden = true;
        const button = this.container.querySelector('[data-lsvp-confirm]');
        button?.setAttribute('aria-busy', 'true');
        const request = this.swapRequest = {};
        try {
          await window.LoftsailsProductSwap.swap(destination, variant);
        } catch (cause) {
          if (cause.name !== 'AbortError' && error) {
            error.hidden = false;
            error.textContent = this.strings.swapError;
          }
        } finally {
          if (this.swapRequest === request) button?.removeAttribute('aria-busy');
        }
        return;
      }

      await this.addToCartAndOpenDrawer(variant);
    }

    /**
     * True when the selected variant belongs to a different product page
     */
    isDifferentProduct(variant) {
      return Boolean(
        variant?.id &&
        String(variant.id) !== String(this.data.currentProduct.id)
      );
    }

    /**
     * Update confirm CTA label/state and express checkout visibility
     */
    updateConfirmButton() {
      if (this.cartPending) return;
      const confirmBtn = this.container.querySelector('[data-lsvp-confirm]');
      const variant = this.findVariant(this.selectedSize, this.selectedConstruction, this.selectedColor);

      if (!confirmBtn) return;

      const showComingSoon = this.updateComingSoon(variant);
      const showBis = this.updateBackInStock(variant);

      if (!variant || !variant.available) {
        confirmBtn.textContent = variant && !variant.available
          ? this.unavailableStockLabel(variant)
          : this.combinedOptions && !this.selectedSize
            ? this.translate('selectSize', 'Select a size')
            : this.translate('notAvailable', 'Not available');
        confirmBtn.disabled = true;
        confirmBtn.hidden = showBis || showComingSoon;
        if (this.expressCheckout) {
          this.expressCheckout.hidden = true;
        }
        return;
      }

      confirmBtn.hidden = false;
      confirmBtn.disabled = false;
      confirmBtn.textContent = this.isDifferentProduct(variant)
        ? this.translate('confirmSelection', 'Confirm Selection')
        : this.translate('addToCart', 'Add to Cart');

      if (this.expressCheckout) {
        this.expressCheckout.hidden = this.isDifferentProduct(variant);
      }
    }

    /**
     * Legacy sail years (e.g. 2023) must not expose back-in-stock signup.
     */
    isBisEligible(variant) {
      if (!variant) return false;
      if (variant.bisEligible === false) return false;

      const modelYear = String(variant.modelYear ?? '').trim();
      if (modelYear === '2023') return false;

      const url = String(variant.url ?? '');
      if (url.includes('-2023')) return false;

      return true;
    }

    /**
     * Point the back-in-stock form at the selected variant and toggle it.
     * Returns true only when the form is shown (variant exists, sold out,
     * eligible, and the form is actually rendered — i.e. the feature is enabled).
     */
    updateComingSoon(variant) {
      if (!this.comingSoonContainer) return false;
      const show = this.isComingSoonVariant(variant);
      this.comingSoonContainer.hidden = !show;
      return show;
    }

    updateBackInStock(variant) {
      if (!this.bisContainer) return false;

      const form = this.bisContainer.querySelector('[data-bis-form]');
      const soldOut = Boolean(
        form && variant && !variant.available && this.isBisEligible(variant)
      );

      if (soldOut && String(form.dataset.bisVariantId) !== String(variant.variantId)) {
        form.bisRequest?.abort();
        delete form.bisRequest;
        delete form.dataset.bisSubscribed;
        const submit = form.querySelector('[data-bis-submit]');
        if (submit) submit.disabled = false;
        form.dataset.bisVariantId = variant.variantId;
        form.dataset.bisProductId = variant.id;
        const status = form.querySelector('[data-bis-status]');
        if (status) {
          status.textContent = '';
          status.className = 'bis-form-2026__status';
        }
      }

      this.bisContainer.hidden = !soldOut;
      return soldOut;
    }

    /**
     * Add product to cart and open the cart drawer
     */
    async addToCartAndOpenDrawer(variant) {
      const confirmBtn = this.container.querySelector('[data-lsvp-confirm]');

      if (this.cartPending) return;
      this.cartPending = true;
      let added = false;
      let rejected = false;

      // Native disabled state also suppresses keyboard activation.
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.setAttribute('aria-busy', 'true');
        confirmBtn.textContent = this.translate('adding', 'Adding...');
      }

      try {
        // Get sections to bundle (for cart drawer update)
        let sectionsToBundle = new Set();
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        // Add to cart via Shopify AJAX API
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: [{
              id: variant.variantId,
              quantity: 1
            }],
            sections: [...sectionsToBundle].join(',')
          })
        });

        if (!response.ok) {
          rejected = response.status >= 400 && response.status < 500 && response.status !== 422;
          throw new Error(this.translate('addError', 'Could not add to cart'));
        }

        added = true;
        const cartResponse = await response.json();

        // Update summary display
        this.updateSummaryDisplay();

        // Close modal
        this.closeModal();

        // Fetch updated cart for the cart:change event
        const cartRead = await fetch(`${Shopify.routes.root}cart.js`);
        if (!cartRead.ok) throw new Error('Cart refresh failed');
        const cartData = await cartRead.json();

        // Merge sections from add response
        if (cartResponse.sections) {
          cartData.sections = cartResponse.sections;
        }

        // Dispatch cart:change event (this triggers cart drawer to open)
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartData
            }
          })
        );

        // Also dispatch variant:add for any listeners
        document.documentElement.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: cartResponse.items || [],
              cart: cartData
            }
          })
        );

      } catch (error) {
        console.error('LSVP: Error adding to cart', error);

        if (added || !rejected) {
          window.location.assign(`${Shopify.routes.root}cart`);
          return;
        }

        // Only a confirmed rejection may invite another add.
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: error.message || this.translate('addError', 'Could not add to cart'),
              tone: 'error'
            }
          })
        );
      } finally {
        this.cartPending = false;
        // Reconcile against the selection, which can change during the request.
        if (confirmBtn) {
          confirmBtn.removeAttribute('aria-busy');
          this.updateConfirmButton();
        }
      }
    }

    /**
     * Update the summary display in the main view
     */
    updateSummaryDisplay() {
      const sizeEl = this.container.querySelector('.lsvp__selected-size');
      const constructionEl = this.container.querySelector('.lsvp__selected-construction');
      const colorEl = this.container.querySelector('.lsvp__selected-color');
      const colorDotEl = this.container.querySelector('.lsvp__summary .lsvp__color-dot');

      if (sizeEl) {
        sizeEl.textContent = this.selectedSize || (this.combinedOptions ? this.translate('selectSize', 'Select a size') : '');
      }

      if (constructionEl) {
        constructionEl.textContent = this.combinedOptions ? this.formatOption() : this.formatConstruction(this.selectedConstruction);
      }

      if (colorEl) {
        colorEl.textContent = this.formatColor(this.selectedColor);
      }

      // Update color swatch image
      if (colorDotEl) {
        const variant = this.findVariant(this.selectedSize, this.selectedConstruction, this.selectedColor);
        colorDotEl.style.backgroundImage = variant && variant.image
          ? `url('${variant.image}')`
          : '';
      }
    }

    /**
     * Update the express checkout form with current variant ID
     */
    updateExpressCheckout() {
      if (!this.expressVariantInput) return;

      const variant = this.findVariant(this.selectedSize, this.selectedConstruction, this.selectedColor);
      if (variant && variant.available && !this.isDifferentProduct(variant)) {
        this.expressVariantInput.value = variant.variantId;
      } else if (this.combinedOptions) {
        this.expressVariantInput.value = '';
      }
    }

    /**
     * Update the price display in the modal
     */
    updatePriceDisplay() {
      const priceDisplay = document.getElementById(`lsvp-price-${this.sectionId}`);
      if (!priceDisplay) return;

      // Selection text always reflects the current picks, even when no
      // variant matches them (e.g. a sold-out combination).
      const sizeEl = priceDisplay.querySelector('[data-lsvp-price-size]');
      const constructionEl = priceDisplay.querySelector('.lsvp__price-construction');
      const colorEl = priceDisplay.querySelector('.lsvp__price-color');
      if (sizeEl) {
        sizeEl.textContent = this.selectedSize;
        if (this.combinedOptions) sizeEl.parentElement.hidden = !this.selectedSize;
      }
      if (constructionEl) constructionEl.textContent = this.combinedOptions ? this.formatOption() : this.formatConstruction(this.selectedConstruction);
      if (colorEl) colorEl.textContent = this.formatColor(this.selectedColor);

      const variant = this.findVariant(this.selectedSize, this.selectedConstruction, this.selectedColor);
      const priceEl = priceDisplay.querySelector('.lsvp__price-value');
      const compareEl = priceDisplay.querySelector('.lsvp__price-compare');

      if (!variant) {
        if (priceEl) priceEl.textContent = '';
        if (compareEl) compareEl.style.display = 'none';
        return;
      }

      if (priceEl) {
        priceEl.textContent = variant.priceFormatted;
      }

      if (compareEl) {
        if (variant.comparePrice && variant.comparePrice > variant.price) {
          compareEl.textContent = variant.comparePriceFormatted;
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
    }

    /**
     * Human label for blank construction.
     */
    formatOption() {
      return this.selectedColor === 'Green'
        ? this.translate('ltdGreen', 'LTD Green')
        : [this.selectedConstruction, this.selectedColor].filter(Boolean).join(' ');
    }

    formatConstruction(construction) {
      return construction || this.translate('standard', 'Standard');
    }

    /**
     * Human label for blank color.
     */
    formatColor(color) {
      return color || this.translate('default', 'Default');
    }
  }

  /**
   * Initialize all variant pickers on the page
   */
  function initVariantPickers() {
    const pickers = document.querySelectorAll('.lsvp[data-section-id]');
    pickers.forEach(picker => {
      new LoftsailsVariantPicker(picker);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantPickers);
  } else {
    initVariantPickers();
  }

  // Re-initialize when Shopify section is reloaded (Theme Editor)
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    const picker = section.querySelector('.lsvp[data-section-id]');
    if (picker) {
      new LoftsailsVariantPicker(picker);
    }
  });

  document.addEventListener('loftsails:product:updated', initVariantPickers);
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('.lsvp').forEach(picker => picker.loftsailsPicker?.destroy());
  });

  // Expose class globally for debugging
  window.LoftsailsVariantPicker = LoftsailsVariantPicker;

})();
